/**
 * Receitas — separadas por fonte, e somadas.
 *
 * O painel que existia mostrava um número de "receita" que era, na verdade, o
 * volume transaccionado. Numa obra de 10 milhões passam quase 12 milhões pela
 * conta da Dozy e a receita são 700 mil: um painel que os junta faz a empresa
 * parecer dezassete vezes maior do que é, e leva a decisões tomadas sobre um
 * número que não existe.
 *
 * Este ecrã separa quatro coisas e recusa-se a somá-las todas:
 *
 *   Volume        o que passa pela plataforma — não é nosso
 *   Receita       taxas e comissões — é o que fica
 *   Do Estado     IVA e retenções — passa cá dentro e sai
 *   Contratado    o que os contratos prometem, ao lado do que já se realizou
 *
 * A última coluna é a que dá mais trabalho a explicar e a única honesta: um
 * contrato assinado promete a taxa toda, mas a obra pode ir em 25%. Prever com
 * o contratado e prestar contas com o realizado são coisas diferentes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { DollarSign, TrendingUp, Layers, Landmark, MapPin, Briefcase } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getReceitas, getReceitasMensais } from '../utils/api';

const kz = (v) => `${Number(v || 0).toLocaleString('pt-PT', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} Kz`;

/** Períodos em datas ISO, calculados no cliente para o servidor ficar simples. */
const PERIODOS = [
  { chave: 'mes', rotulo: 'Este mês' },
  { chave: 'trimestre', rotulo: 'Trimestre' },
  { chave: 'ano', rotulo: 'Este ano' },
  { chave: 'tudo', rotulo: 'Desde o início' },
];

function intervalo(chave) {
  const hoje = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (chave === 'mes') return { de: iso(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), ate: iso(hoje) };
  if (chave === 'trimestre') {
    const t = Math.floor(hoje.getMonth() / 3) * 3;
    return { de: iso(new Date(hoje.getFullYear(), t, 1)), ate: iso(hoje) };
  }
  if (chave === 'ano') return { de: iso(new Date(hoje.getFullYear(), 0, 1)), ate: iso(hoje) };
  return {};
}

const Cartao = ({ icone: Icone, rotulo, valor, nota, destaque }) => (
  <div className={`rounded-xl p-4 border ${destaque
    ? 'bg-teal-500/10 border-teal-500/40'
    : 'bg-gray-800 border-gray-700'}`}>
    <div className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wide">
      <Icone size={14} /> {rotulo}
    </div>
    <div className={`mt-1 font-bold tabular-nums ${destaque ? 'text-teal-300 text-2xl' : 'text-white text-xl'}`}>
      {valor}
    </div>
    {nota && <div className="mt-1 text-xs text-gray-500">{nota}</div>}
  </div>
);

const Ranking = ({ titulo, nota, icone: Icone, dados = [] }) => {
  const max = Math.max(...dados.map((x) => Number(x.receita)), 1);
  return (
    <section className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <header className="px-4 py-3 border-b border-gray-700">
        <h3 className="text-white font-semibold flex items-center gap-2"><Icone size={16} className="text-teal-400" /> {titulo}</h3>
        <p className="text-gray-500 text-xs mt-0.5">{nota}</p>
      </header>
      <ol className="p-4 space-y-4">
        {dados.length === 0 && <li className="text-sm text-gray-500">Ainda não há receita neste período.</li>}
        {dados.slice(0, 8).map((x, i) => (
          <li key={x.nome}>
            <div className="flex items-baseline gap-2 text-sm">
              <span className={`grid place-items-center w-6 h-6 rounded-full text-xs font-bold ${i === 0 ? 'bg-amber-500/20 text-amber-300' : 'bg-gray-700 text-gray-400'}`}>{i + 1}</span>
              <strong className="text-gray-200 truncate">{x.nome}</strong>
              <span className="ml-auto text-white font-semibold tabular-nums">{kz(x.receita)}</span>
            </div>
            <div className="ml-8 mt-1.5 h-1.5 bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-teal-500 to-cyan-400 rounded-full" style={{ width: `${Number(x.receita) > 0 ? Math.max(1, (Number(x.receita) / max) * 100) : 0}%` }} />
            </div>
            <div className="ml-8 mt-1 text-[11px] text-gray-500">{x.servicos} {x.servicos === 1 ? 'serviço' : 'serviços'} · volume {kz(x.volume)}</div>
          </li>
        ))}
      </ol>
    </section>
  );
};

const ReceitasPage = () => {
  const [periodo, setPeriodo] = useState('mes');
  const [d, setD] = useState(null);
  const [meses, setMeses] = useState([]);
  const [erro, setErro] = useState('');

  const carregar = useCallback(() => {
    setErro('');
    setD(null);
    getReceitas(intervalo(periodo))
      .then(setD)
      .catch((e) => setErro(e?.response?.data?.message || 'Não foi possível carregar as receitas.'));
  }, [periodo]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { getReceitasMensais(12).then(setMeses).catch(() => setMeses([])); }, []);

  if (erro) return <div className="text-red-400 text-sm">{erro}</div>;
  if (!d) return <LoadingSpinner />;

  const maxMes = Math.max(...meses.map((m) => m.total), 1);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Receitas</h1>
          <p className="text-gray-400 text-sm mt-1">
            O que é da Dozy, separado do que só lhe passa pelas mãos.
          </p>
        </div>
        <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
          {PERIODOS.map((p) => (
            <button
              key={p.chave}
              onClick={() => setPeriodo(p.chave)}
              className={`px-3 py-1.5 rounded-md text-sm transition ${periodo === p.chave
                ? 'bg-teal-500 text-white' : 'text-gray-400 hover:text-white'}`}
            >{p.rotulo}</button>
          ))}
        </div>
      </div>

      {/* Os quatro números que não se somam entre si. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Cartao
          icone={DollarSign} destaque
          rotulo="Receita realizada" valor={kz(d.receita.realizada)}
          nota={`${kz(d.receita.contratada)} contratados`}
        />
        <Cartao
          icone={Layers} rotulo="Volume intermediado" valor={kz(d.volume.total)}
          nota="Não é receita — é o que passa pela plataforma"
        />
        <Cartao
          icone={TrendingUp} rotulo="Taxa efectiva" valor={`${d.take_rate_pct}%`}
          nota="Intermediação sobre volume intermediado"
        />
        <Cartao
          icone={Landmark} rotulo="Do Estado" valor={kz(d.terceiros.iva_sobre_taxas + d.terceiros.retencoes)}
          nota={`IVA ${kz(d.terceiros.iva_sobre_taxas)} · retenções ${kz(d.terceiros.retencoes)}`}
        />
      </div>

      {/* Receita por fonte. Contratado e realizado lado a lado. */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-700">
          <h2 className="text-white font-semibold">Por fonte</h2>
          <p className="text-gray-500 text-xs mt-0.5">
            Contratado é o que os contratos prometem; realizado segue as etapas
            que o cliente já aprovou.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="text-gray-400 text-xs uppercase">
              <tr>
                <th className="text-left font-medium px-4 py-2">Fonte</th>
                <th className="text-left font-medium px-4 py-2">Tipo</th>
                <th className="text-right font-medium px-4 py-2">Contratado</th>
                <th className="text-right font-medium px-4 py-2">Realizado</th>
                <th className="text-right font-medium px-4 py-2">N.º</th>
              </tr>
            </thead>
            <tbody>
              {d.linhas.map((l) => (
                <tr key={l.fonte} className="border-t border-gray-700/60">
                  <td className="px-4 py-2.5 text-white">
                    {l.rotulo}
                    <span className="block text-xs text-gray-500">Dozy {l.produto}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${l.tipo === 'DIRECTA'
                      ? 'bg-blue-500/15 text-blue-300' : 'bg-gray-700 text-gray-300'}`}>
                      {d.rotulos_tipo[l.tipo]}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-400 tabular-nums">{kz(l.contratado)}</td>
                  <td className="px-4 py-2.5 text-right text-white font-semibold tabular-nums">{kz(l.realizado)}</td>
                  <td className="px-4 py-2.5 text-right text-gray-500 tabular-nums">{l.registos}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-600 bg-gray-800/80">
                <td className="px-4 py-3 text-white font-semibold" colSpan={2}>Total</td>
                <td className="px-4 py-3 text-right text-gray-300 font-semibold tabular-nums">
                  {kz(d.receita.contratada)}
                </td>
                <td className="px-4 py-3 text-right text-teal-300 font-bold tabular-nums">
                  {kz(d.receita.realizada)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Os mesmos números, agrupados de duas maneiras diferentes. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
          <h3 className="text-white font-semibold text-sm mb-3">Por produto</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Dozy Home</span>
              <span className="text-white tabular-nums">{kz(d.receita.home)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Dozy Business</span>
              <span className="text-white tabular-nums">{kz(d.receita.business)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-700 pt-2">
              <span className="text-gray-300 font-medium">Conjunto</span>
              <span className="text-teal-300 font-bold tabular-nums">{kz(d.receita.realizada)}</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Volume: Home {kz(d.volume.home)} · Business {kz(d.volume.business)}
          </p>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
          <h3 className="text-white font-semibold text-sm mb-3">Por natureza</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Intermediação</span>
              <span className="text-white tabular-nums">{kz(d.receita.intermediacao)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Serviços vendidos pela Dozy</span>
              <span className="text-white tabular-nums">{kz(d.receita.directa)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-700 pt-2">
              <span className="text-gray-300 font-medium">Conjunto</span>
              <span className="text-teal-300 font-bold tabular-nums">{kz(d.receita.realizada)}</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Só a intermediação entra na taxa efectiva: numa avaliação técnica a Dozy
            vende um serviço seu e fica com tudo.
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Ranking
          titulo="Receita por sector"
          nota="Categorias ordenadas pela receita realizada da Dozy."
          icone={Briefcase}
          dados={d.rankings?.sectores || []}
        />
        <Ranking
          titulo="Receita por zona"
          nota="Zonas ordenadas pela receita realizada; moradas Home sem correspondência ficam identificadas."
          icone={MapPin}
          dados={d.rankings?.zonas || []}
        />
      </div>

      {/* Evolução. Empilhada por fonte, porque a pergunta é sempre "de onde
          veio o crescimento" e não "quanto foi o total". */}
      {meses.length > 0 && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
          <h3 className="text-white font-semibold text-sm mb-4">Últimos 12 meses</h3>
          <div className="flex items-end gap-1.5 h-40">
            {meses.map((m) => (
              <div key={m.mes} className="flex-1 flex flex-col justify-end items-center gap-1 group">
                <div
                  className="w-full flex flex-col justify-end"
                  style={{ height: `${(m.total / maxMes) * 100}%`, minHeight: m.total > 0 ? '3px' : '0' }}
                  title={`${m.mes}: ${kz(m.total)}`}
                >
                  <div className="bg-blue-500" style={{ height: `${(m.avaliacoes / (m.total || 1)) * 100}%` }} />
                  <div className="bg-teal-500" style={{ height: `${(m.business / (m.total || 1)) * 100}%` }} />
                  <div className="bg-amber-500" style={{ height: `${(m.home / (m.total || 1)) * 100}%` }} />
                </div>
                <span className="text-[10px] text-gray-500 group-hover:text-gray-300">
                  {m.mes.slice(5)}
                </span>
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-3 text-xs text-gray-400">
            <span className="flex items-center gap-1.5">
              <i className="w-2.5 h-2.5 rounded-sm bg-amber-500 inline-block" /> Home
            </span>
            <span className="flex items-center gap-1.5">
              <i className="w-2.5 h-2.5 rounded-sm bg-teal-500 inline-block" /> Business
            </span>
            <span className="flex items-center gap-1.5">
              <i className="w-2.5 h-2.5 rounded-sm bg-blue-500 inline-block" /> Avaliações
            </span>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500">
        A receita realizada do Business acompanha as etapas aprovadas pelo
        cliente. Um contrato sem plano de etapas conta como 0% realizado — é
        conservador de propósito, para o painel nunca contar dinheiro antes de
        o trabalho estar aceite.
      </p>
    </div>
  );
};

export default ReceitasPage;
